// POC: persists the age/legal-drinking-age consent answer per table. windowTimeInSeconds
// (config-driven) controls how long an answer is remembered before we ask again.
import { getFromStorage, saveToStorage } from "./storage";
import { STORAGE_KEYS } from "../constants/StorageKeys";

const readStoredConsent = async (tableId: string): Promise<{ consent: boolean; savedAt: number } | null> => {
    if (!tableId) return null;
    try {
        const response = await getFromStorage(STORAGE_KEYS.DRINKING_AGE_CONSENT);
        const data = JSON.parse(response?.data?.[STORAGE_KEYS.DRINKING_AGE_CONSENT] ?? "{}");
        if (data.tableId !== tableId || typeof data.consent !== "boolean") return null;

        return { consent: data.consent, savedAt: Number(data.savedAt ?? 0) };
    } catch (_) {
        return null;
    }
};

// Returns null if unanswered/expired, otherwise the user's prior agree/decline.
export const getDrinkingAgeConsent = async (tableId: string, windowTimeInSeconds: number): Promise<boolean | null> => {
    const stored = await readStoredConsent(tableId);
    if (!stored) return null;

    const isExpired = Date.now() - stored.savedAt >= windowTimeInSeconds * 1000;
    return isExpired ? null : stored.consent;
};

// Early hint for the optimistic prefetch guess, used before config (and its window) has
// loaded — ignores expiry, since we're only picking a better guess than a hardcoded default,
// not deciding whether to show the gate. getDrinkingAgeConsent() still does that authoritative,
// windowed check once config is available, and corrects the guess if this hint was stale.
export const peekDrinkingAgeConsent = async (tableId: string): Promise<boolean | null> => {
    const stored = await readStoredConsent(tableId);
    return stored ? stored.consent : null;
};

export const setDrinkingAgeConsent = async (tableId: string, consent: boolean): Promise<void> => {
    if (!tableId) return;
    try {
        await saveToStorage(
            STORAGE_KEYS.DRINKING_AGE_CONSENT,
            JSON.stringify({ tableId, consent, savedAt: Date.now() }),
        );
    } catch (_) {}
};
